/**
 * BatchProcessDocuments - Staff utility for batch processing documents
 */
import { appLogger } from '@annote/core/utils/debug';
import React, { useState, useRef, useEffect } from 'react';
import { supabaseClient } from 'src/db/supabase';
import { SUPABASE_URL } from 'src/config/supabase';

const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/process_document`;

interface ProcessingResult {
  total: number;
  processed: number;
  failed: number;
  errors: Array<{
    id: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: any;
  }>;
}

const BatchProcessDocuments: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const abortController = useRef<AbortController | null>(null);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      // Check current auth status
      const { data } = await supabaseClient.auth.getSession();
      appLogger.debug('Auth session:', data);

      if (!data.session) {
        appLogger.error('Not authenticated! This is why queries return nothing.');
      }
    };

    checkAuth();
  }, []);

  const processDocument = async (docId: string, authToken: string, retryCount = 0): Promise<void> => {
    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          // Add origin header to debug CORS issues
          Origin: window.location.origin,
        },
        body: JSON.stringify({ document_id: docId }),
        signal: abortController.current?.signal,
        // Add credentials to ensure cookies are sent if needed
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorDetails = null;

        try {
          // Try to parse the error response as JSON
          const errorJson = JSON.parse(errorText);
          errorDetails = errorJson;

          // Throw a more informative error with full details
          throw new Error(
            `Failed to process document ${docId}: ${response.statusText}${
              errorJson.error ? ` - ${errorJson.error}` : ''
            }`,
            { cause: errorDetails },
          );
        } catch (parseError) {
          // If parsing fails, use the raw error text
          throw new Error(
            `Failed to process document ${docId}: ${response.statusText}${errorText ? ` - ${errorText}` : ''}`,
          );
        }
      }
    } catch (err) {
      // Check specifically for CORS errors
      const isCorsError =
        err instanceof Error &&
        (err.message.includes('Access-Control-Allow-Origin') ||
          err.message.includes('CORS') ||
          err.message.includes('cross-origin'));

      if (isCorsError) {
        appLogger.error(`CORS error for document ${docId}:`, err.message);
        throw new Error(`CORS error processing document ${docId} - please check server configuration`, {
          cause: { type: 'cors_error', docId, originalError: err.message },
        });
      }

      // Retry logic for network errors (up to 3 retries)
      const MAX_RETRIES = 3;
      const isNetworkError =
        err instanceof Error &&
        (err.message.includes('Load failed') ||
          err.message.includes('Failed to fetch') ||
          err.message.includes('Network error'));

      if (isNetworkError && retryCount < MAX_RETRIES) {
        appLogger.debug(`Network error for document ${docId}, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        // Wait a bit longer between each retry attempt
        const delay = (retryCount + 1) * 1000; // Increasing delay: 1s, 2s, 3s
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, delay);
        });

        // Retry the operation
        await processDocument(docId, authToken, retryCount + 1);

        return;
      }

      // If we've reached max retries or it's not a network error, rethrow
      throw err;
    }
  };

  const stopProcessing = (): void => {
    if (abortController.current) {
      abortController.current.abort();
      setIsProcessing(false);
    }
  };

  const toggleErrorDetails = (docId: string): void => {
    if (expandedError === docId) {
      setExpandedError(null);
    } else {
      setExpandedError(docId);
    }
  };

  const startProcessing = async (): Promise<void> => {
    setIsProcessing(true);
    setError(null);
    setExpandedError(null);
    abortController.current = new AbortController();

    try {
      // Get documents that need processing (only filter by embedding = null)
      const { data: documents, error: queryError } = await supabaseClient
        .from('Notes')
        .select('id')
        .is('embedding', null)
        .is('deletedAt', null)
        .or('title.neq.null,title.neq."",value.neq.null,value.neq.""');

      if (queryError) throw queryError;

      if (!documents?.length) {
        setResult({ total: 0, processed: 0, failed: 0, errors: [] });
        return;
      }

      const results: ProcessingResult = {
        total: documents.length,
        processed: 0,
        failed: 0,
        errors: [],
      };
      setResult(results);

      const session = await supabaseClient.auth.getSession();
      const authToken = session.data.session?.access_token;
      if (!authToken) throw new Error('No auth token available');

      for (let i = 0; i < documents.length; i += 1) {
        if (abortController.current?.signal.aborted) {
          break;
        }

        const doc = documents[i];
        try {
          // eslint-disable-next-line no-await-in-loop
          await processDocument(doc.id, authToken);
          // sleep for 2 seconds
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
          results.processed = i + 1;
          setResult({ ...results });
        } catch (err) {
          results.failed += 1;

          // Better error handling - extract more details if possible
          let errorMessage = '';
          let errorDetails = null;

          if (err instanceof Error) {
            errorMessage = err.message;

            // Get the cause if available (from the enhanced error handling in processDocument)
            if ('cause' in err) {
              errorDetails = err.cause;
            } else {
              // Try the old method of extracting JSON from error message if needed
              try {
                const jsonMatch = err.message.match(/\{.*\}/s);
                if (jsonMatch) {
                  errorDetails = JSON.parse(jsonMatch[0]);
                }
              } catch (e) {
                // Ignore parsing errors
              }
            }

            // If we have a stack trace, include it in the details
            if (err.stack) {
              errorDetails = {
                ...(errorDetails || {}),
                stack: err.stack.split('\n').slice(0, 3).join('\n'),
              };
            }
          } else {
            errorMessage = String(err);
          }

          results.errors.push({
            id: doc.id,
            message: errorMessage,
            details: errorDetails,
          });

          setResult({ ...results });
        }

        // Small delay between requests to avoid overwhelming the server
        if (i < documents.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 300); // Increased delay to 300ms
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsProcessing(false);
      abortController.current = null;
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
      <h1 style={{ fontSize: '24px', marginBottom: '20px' }}>Batch Process Documents</h1>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>Function URL: {FUNCTION_URL}</div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={startProcessing}
          disabled={isProcessing}
          style={{
            padding: '10px 20px',
            backgroundColor: isProcessing ? '#ccc' : '#3ECF8E',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isProcessing ? 'default' : 'pointer',
          }}
        >
          {isProcessing ? 'Processing...' : 'Start Processing'}
        </button>

        {isProcessing && (
          <button
            onClick={stopProcessing}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Stop
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <div
              style={{
                width: '100%',
                height: '20px',
                backgroundColor: '#e5e7eb',
                borderRadius: '10px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(result.processed / result.total) * 100}%`,
                  height: '100%',
                  backgroundColor: '#3ECF8E',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ marginTop: '5px', color: '#666' }}>
              Processed {result.processed} of {result.total} documents
              {result.failed > 0 && ` (${result.failed} failed)`}
            </div>
          </div>

          {result.errors.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: '#dc2626', marginBottom: '10px' }}>Errors ({result.errors.length}):</h3>
              <div
                style={{
                  backgroundColor: '#fee2e2',
                  padding: '10px',
                  borderRadius: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                }}
              >
                {result.errors.map((err) => (
                  <div
                    key={err.id}
                    style={{ marginBottom: '8px', borderBottom: '1px solid #fca5a5', paddingBottom: '8px' }}
                  >
                    <div
                      onClick={() => toggleErrorDetails(err.id)}
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#dc2626',
                      }}
                    >
                      <div>Error processing document {err.id}</div>
                      <div>{expandedError === err.id ? '▲' : '▼'}</div>
                    </div>

                    {expandedError === err.id && (
                      <div
                        style={{
                          marginTop: '5px',
                          padding: '8px',
                          backgroundColor: '#fff1f2',
                          fontSize: '14px',
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        <div>
                          <strong>Error:</strong> {String(err.message)}
                        </div>
                        {err.details && (
                          <div style={{ marginTop: '8px' }}>
                            <strong>Details:</strong>
                            <pre style={{ marginTop: '4px', fontSize: '12px' }}>
                              {JSON.stringify(err.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchProcessDocuments;
